import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../assets/scss/Profile.module.scss';
import settingStyles from '../../assets/scss/Settings/Settings.module.scss';
import { getUserInfo } from '../../utils/user.util';
import { useGetUserByIdQuery } from '../../slices/admin/admin';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BaseURL } from '../../lib/api';
import dummyProfile from "../../assets/images/Profile.png";

const OrganizationSettings = () => {
    const user = getUserInfo();
    const userId = user?._id;
    const navigate = useNavigate();

    const { data, isLoading, error, refetch } = useGetUserByIdQuery(userId, { skip: !userId });

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        userName: '',
        organizationName: '',
        gst: '',
        address1: '',
        address2: '',
        pincode: '',
        city: '',
        state: '',
        // keeping bank details to not overwrite with blank
        paymentDetails: 'bank',
        bankName: '',
        bankAccount: '',
        bankIfsc: '',
        upiId: '',
    });

    const [errors, setErrors] = useState({});
    const [orgLogoFile, setOrgLogoFile] = useState(null);
    const [orgLogoPreview, setOrgLogoPreview] = useState(dummyProfile);

    const handleOrgLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setOrgLogoFile(file);
            setOrgLogoPreview(URL.createObjectURL(file));
        }
    };

    useEffect(() => {
        if (data?.result?.length > 0) {
            const u = data.result[0];
            const bank = u.banks?.[0] || {};
            const store = u.store || {};

            setFormData((prev) => ({
                ...prev,
                firstName: u.firstName || '',
                lastName: u.lastName || '',
                email: u.email || '',
                phone: u.phone || '',
                userName: u.userName || '',
                organizationName: store.storeName || '',
                gst: store.gst || '',
                address1: store.address1 || '',
                address2: store.address2 || '',
                pincode: store.pincode?.toString() || '',
                city: store.city || '',
                state: store.state || '',
                bankName: bank.bankName || '',
                bankAccount: bank.accountNo || '',
                bankIfsc: bank.ifsc || '',
                upiId: bank.upiId || '',
                paymentDetails: bank.paymentType || 'bank',
            }));

            if (store.logo) {
                const logoUrl = store.logo.startsWith("http")
                    ? store.logo
                    : `${BaseURL}/${store.logo}`;
                setOrgLogoPreview(logoUrl);
            } else {
                setOrgLogoPreview(dummyProfile);
            }
        }
    }, [data]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.organizationName) newErrors.organizationName = 'Organization name is required';
        if (!formData.address1) newErrors.address1 = 'Address1 is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.pincode) newErrors.pincode = 'Pincode is required';

        return newErrors;
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            const payload = new FormData();

            // Basic fields (always required to not overwrite with blank if backend requires)
            payload.append('firstName', formData.firstName);
            payload.append('lastName', formData.lastName);
            payload.append('userName', formData.userName);
            payload.append('email', formData.email || '');
            payload.append('phone', formData.phone);

            payload.append('storeName', formData.organizationName || '');
            payload.append('address1', formData.address1 || '');
            payload.append('address2', formData.address2 || '');
            payload.append('pincode', formData.pincode || '');
            payload.append('city', formData.city || '');
            payload.append('state', formData.state || '');
            payload.append('gst', formData.gst || '');

            payload.append('bankName', formData.bankName || '');
            payload.append('accountNo', formData.bankAccount || '');
            payload.append('ifsc', formData.bankIfsc || '');
            payload.append('paymentType', formData.paymentDetails?.toLowerCase() || '');
            payload.append('upiId', formData.upiId || '');

            if (orgLogoFile) {
                payload.append('logo', orgLogoFile);
            }

            try {
                const response = await axios.put(
                    `${BaseURL}/user/updateProfile/${userId}`,
                    payload,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${user?.token}`,
                        },
                    }
                );

                toast.success(response.data.message || 'Organization Details updated successfully');
                refetch();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to update organization details');
            }
        }
    };

    if (isLoading) return <p>Loading organization details...</p>;
    if (error) return <p>Error loading organization details.</p>;

    return (
        <div className={styles.profileContainerinside} style={{ marginTop: '20px' }}>
            <div className={settingStyles.headerRow}>
                <h2 className={settingStyles.header}>Organization Detail</h2>
            </div>

            <div className={styles.card}>
                <div className={styles.header}>
                    <h3 className={styles.Heading}>🏢 Organization Detail</h3>
                </div>
                <div className={styles.content}>
                    <div className={styles.formSection}>
                        <div className={styles.inputGroupheader}>
                            <div className={styles.imageContainer}>
                                <img src={orgLogoPreview} alt="org logo" className={styles.profileImage} />
                                <div style={{ marginTop: "20px" }} >
                                    <label htmlFor="orgLogoInput" className={styles.changeImageButton}>
                                        Upload Org Logo
                                    </label>
                                    <input
                                        id="orgLogoInput"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleOrgLogoChange}
                                        style={{ display: 'none' }}
                                    />
                                    <p className={styles.imageInfo}>Upload image below 2MB (JPG, PNG)</p>
                                </div>
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Organization Name</label>
                                <input
                                    type="text"
                                    name="organizationName"
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    placeholder="Organization Name"
                                    className={styles.input}
                                />
                                {errors.organizationName && <span className={styles.error}>{errors.organizationName}</span>}
                            </div>
                        </div>

                        <div style={{ marginTop: "20px" }} className={styles.inputGroupheader}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Address 1</label>
                                <input
                                    type="text"
                                    name="address1"
                                    value={formData.address1}
                                    onChange={handleChange}
                                    placeholder="Address Line 1"
                                    className={styles.input}
                                />
                                {errors.address1 && <span className={styles.error}>{errors.address1}</span>}
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Address 2 / City</label>
                                <input
                                    type="text"
                                    name="address2"
                                    value={formData.address2 || ''}
                                    onChange={handleChange}
                                    placeholder="Address Line 2"
                                    className={styles.input}
                                />
                                {errors.address2 && <span className={styles.error}>{errors.address2}</span>}
                            </div>
                        </div>

                        <div className={styles.inputGroupheader}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Pincode</label>
                                <input
                                    type="number"
                                    name="pincode"
                                    value={formData.pincode || ''}
                                    onChange={async (e) => {
                                        const pin = e.target.value;
                                        setFormData(prev => ({ ...prev, pincode: pin }));
                                        if (/^\d{6}$/.test(pin)) {
                                            try {
                                                const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
                                                const json = await res.json();
                                                const postOffice = json?.[0]?.PostOffice?.[0];
                                                if (postOffice) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        pincode: pin,
                                                        city: postOffice.District,
                                                        state: postOffice.State,
                                                    }));
                                                } else {
                                                    toast.warn('Invalid Pincode');
                                                }
                                            } catch {
                                                toast.error('Failed to fetch address');
                                            }
                                        }
                                    }}
                                    placeholder="Pincode"
                                    className={styles.input}
                                />
                                {errors.pincode && <span className={styles.error}>{errors.pincode}</span>}
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>District</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city || ''}
                                    onChange={handleChange}
                                    className={styles.input}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className={styles.inputGroupheader}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>State</label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state || ''}
                                    onChange={handleChange}
                                    className={styles.input}
                                    disabled
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>GST</label>
                                <input
                                    type="text"
                                    name="gst"
                                    value={formData.gst}
                                    onChange={handleChange}
                                    placeholder="GST"
                                    className={styles.input}
                                />
                                {errors.gst && <span className={styles.error}>{errors.gst}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.buttonGroup} style={{ marginTop: '20px' }}>
                    <button className={styles.cancelButton} onClick={() => navigate('/dashboard')}>Cancel</button>
                    <button className={styles.saveButton} onClick={handleSubmit}>
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrganizationSettings;
